import { createFileRoute, Link } from '@tanstack/react-router';
import { Fragment, useEffect, useState } from 'react';
import {
  PAYROLL_COMPONENT_CATALOG,
  PAYROLL_GROUP_ORDER,
  type ApplyOwnerReimbursementResult,
  type PayrollComponentGroup,
  type PayrollReimbursableEmployee,
} from '@accounting/shared';
import {
  useApplyOwnerReimbursement,
  usePayrollMappings,
  useReimbursableEmployees,
  useUpdatePayrollMappings,
  useUpdateReimbursableEmployees,
} from '../../hooks/usePayroll';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/payroll/mappings')({
  component: PayrollMappingsPage,
});

const COMPONENTS_BY_GROUP = PAYROLL_GROUP_ORDER.map((g) => ({
  ...g,
  components: PAYROLL_COMPONENT_CATALOG.filter((c) => c.group === (g.group as PayrollComponentGroup)),
}));

function PayrollMappingsPage() {
  const companyId = useActiveCompanyId();
  const { data: accounts = [] } = useAccounts(companyId);
  const { data: mappings, isLoading } = usePayrollMappings(companyId);
  const save = useUpdatePayrollMappings(companyId);

  const isPostable = (a: typeof accounts[number]) => a.isActive && a.allowDirectPosting;
  const [sel, setSel] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!mappings) return;
    const next: Record<string, string> = {};
    for (const m of mappings) next[m.componentType] = m.accountId;
    setSel(next);
  }, [mappings]);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  const saveErr =
    save.error instanceof ApiError
      ? (save.error.body as { error?: string } | null)?.error ?? 'Save failed'
      : save.error?.message;

  const submit = () => {
    const list = PAYROLL_COMPONENT_CATALOG.filter((c) => sel[c.type]).map((c) => ({
      componentType: c.type,
      accountId: sel[c.type]!,
    }));
    if (list.length > 0) save.mutate({ mappings: list });
  };

  return (
    <div>
      <Link to="/payroll" style={{ fontSize: 13 }}>← Payroll</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 6px' }}>Payroll GL Mapping</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Each payroll component posts to the account selected here. Defaults are seeded from the standard
        chart of accounts; change any that don’t match your books.
      </p>

      {isLoading && <p className="muted">Loading…</p>}

      {COMPONENTS_BY_GROUP.map((group) => (
        <div key={group.group} className="card" style={{ padding: 16, marginBottom: 12, maxWidth: 760 }}>
          <h2 style={{ fontSize: 14, marginTop: 0, marginBottom: 10 }}>{group.label}</h2>
          <div className="form-grid" style={{ marginBottom: 0 }}>
            {group.components.map((c) => (
              <Fragment key={c.type}>
                <label htmlFor={`m-${c.type}`}>
                  {c.label}
                  <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>
                    ({c.side === 'debit' ? 'Dr' : 'Cr'})
                  </span>
                </label>
                <AccountPicker
                  id={`m-${c.type}`}
                  accounts={accounts}
                  value={sel[c.type] ?? ''}
                  onChange={(id) => setSel((prev) => ({ ...prev, [c.type]: id }))}
                  predicate={isPostable}
                  placeholder="— not mapped —"
                />
              </Fragment>
            ))}
          </div>
        </div>
      ))}

      {saveErr && <div className="field-error" style={{ marginBottom: 8 }}>{saveErr}</div>}
      {save.isSuccess && <div className="tag ok" style={{ marginBottom: 8 }}>Saved</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save mapping'}
        </button>
        <Link to="/payroll" className="btn-secondary" style={{ textDecoration: 'none' }}>Done</Link>
      </div>

      <ReimbursableEmployees companyId={companyId} accounts={accounts} />
    </div>
  );
}

type AccountRow = ReturnType<typeof useAccounts>['data'] extends (infer T)[] | undefined ? T : never;

interface RowDraft {
  id?: string;
  employeeName: string;
  sharePercent: string;
  receivableAccountId: string;
  contraAccountId: string;
  isActive: boolean;
}

const fromServer = (e: PayrollReimbursableEmployee): RowDraft => ({
  id: e.id,
  employeeName: e.employeeName,
  sharePercent: e.sharePercent,
  receivableAccountId: e.receivableAccountId,
  contraAccountId: e.contraAccountId,
  isActive: e.isActive,
});

/**
 * Employees whose payroll cost the owner pays back to the company.
 *
 * Each of their stubs posts an extra balanced pair on its own entry —
 * DR the receivable (Due from Owner), CR the contra expense — for the chosen
 * share of the employer cost. Nobody else's stub is touched, so the owner
 * paying himself through the same payroll never picks it up.
 */
function ReimbursableEmployees({ companyId, accounts }: { companyId: string; accounts: AccountRow[] }) {
  const { data: employees, isLoading } = useReimbursableEmployees(companyId);
  const save = useUpdateReimbursableEmployees(companyId);
  const [rows, setRows] = useState<RowDraft[]>([]);

  useEffect(() => {
    if (employees) setRows(employees.map(fromServer));
  }, [employees]);

  const isAsset = (a: AccountRow) => a.isActive && a.allowDirectPosting && a.accountType === 'asset';
  const isExpense = (a: AccountRow) => a.isActive && a.allowDirectPosting && a.accountType === 'expense';

  const update = (i: number, patch: Partial<RowDraft>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const saveErr =
    save.error instanceof ApiError
      ? (save.error.body as { error?: string } | null)?.error ?? 'Save failed'
      : save.error?.message;

  const complete = rows.every(
    (r) => r.employeeName.trim() && r.receivableAccountId && r.contraAccountId && r.sharePercent.trim(),
  );

  return (
    <div className="card" style={{ padding: 16, marginTop: 24, maxWidth: 760 }}>
      <h2 style={{ fontSize: 14, marginTop: 0, marginBottom: 4 }}>Owner-reimbursed employees</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        When a listed person is paid, their employer cost (gross wages plus employer taxes and
        benefits) is charged back to the owner on the same journal entry: debit the receivable,
        credit the contra expense. Wages still show W-2 gross; the contra nets the P&amp;L to zero;
        the receivable clears when the owner deposits the money. Anyone not listed posts as usual.
      </p>

      {isLoading && <p className="muted">Loading…</p>}

      {rows.map((r, i) => (
        <div
          key={r.id ?? `new-${i}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 80px 1fr 1fr auto auto',
            gap: 8,
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <input
            value={r.employeeName}
            onChange={(e) => update(i, { employeeName: e.target.value })}
            placeholder="Employee name as it appears on the register"
            maxLength={160}
            title="Matched to each paystub by name, ignoring case and spacing"
          />
          <input
            value={r.sharePercent}
            onChange={(e) => update(i, { sharePercent: e.target.value })}
            inputMode="decimal"
            placeholder="%"
            title="Share of this person's cost the owner bears, 0–100"
            style={{ textAlign: 'right' }}
          />
          <AccountPicker
            accounts={accounts}
            value={r.receivableAccountId}
            onChange={(id) => update(i, { receivableAccountId: id })}
            predicate={isAsset}
            placeholder="— receivable (Due from Owner) —"
            title="Asset account the owner owes into"
          />
          <AccountPicker
            accounts={accounts}
            value={r.contraAccountId}
            onChange={(id) => update(i, { contraAccountId: id })}
            predicate={isExpense}
            placeholder="— contra expense —"
            title="Expense account that takes the credit"
          />
          <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={r.isActive}
              onChange={(e) => update(i, { isActive: e.target.checked })}
            />{' '}
            active
          </label>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
            title="Remove. Stubs already posted keep their charge-back."
          >
            ×
          </button>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <button
          type="button"
          className="btn-secondary"
          onClick={() =>
            setRows((prev) => [
              ...prev,
              { employeeName: '', sharePercent: '100', receivableAccountId: '', contraAccountId: '', isActive: true },
            ])
          }
        >
          Add employee
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={save.isPending || !complete}
          onClick={() =>
            save.mutate({
              employees: rows.map((r) => ({
                id: r.id,
                employeeName: r.employeeName.trim(),
                sharePercent: r.sharePercent.trim(),
                receivableAccountId: r.receivableAccountId,
                contraAccountId: r.contraAccountId,
                isActive: r.isActive,
              })),
            })
          }
        >
          {save.isPending ? 'Saving…' : 'Save reimbursed employees'}
        </button>
        {save.isSuccess && <span className="tag ok">Saved</span>}
      </div>
      {saveErr && <div className="field-error" style={{ marginTop: 8 }}>{saveErr}</div>}

      {(employees?.length ?? 0) > 0 && <ApplyToPostedRuns companyId={companyId} />}
    </div>
  );
}

/**
 * Charge back stubs that were posted before their rule existed. Preview
 * first; apply rewrites each stub's own entry in place and skips ones done.
 */
function ApplyToPostedRuns({ companyId }: { companyId: string }) {
  const run = useApplyOwnerReimbursement(companyId);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [preview, setPreview] = useState<ApplyOwnerReimbursementResult | null>(null);
  const [applied, setApplied] = useState<ApplyOwnerReimbursementResult | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const errorText = (err: unknown) =>
    err instanceof ApiError
      ? (err.body as { error?: string } | null)?.error ?? err.message
      : err instanceof Error
        ? err.message
        : String(err);

  const ready = !!from && !!to && from <= to;

  const doPreview = async () => {
    setNote(null);
    setApplied(null);
    try {
      setPreview(await run.mutateAsync({ from, to, dryRun: true }));
    } catch (err) {
      setNote(errorText(err));
    }
  };

  const doApply = async () => {
    if (!preview) return;
    const n = preview.totals.planned;
    const ok = confirm(
      `Charge ${formatCurrency(preview.totals.amountTotal)} back to the owner on ${n} paystub${n === 1 ? '' : 's'}? Each entry stays balanced and reconciled lines are not touched.`,
    );
    if (!ok) return;
    setNote(null);
    try {
      const res = await run.mutateAsync({ from, to, dryRun: false });
      setApplied(res);
      setPreview(await run.mutateAsync({ from, to, dryRun: true }));
    } catch (err) {
      setNote(errorText(err));
    }
  };

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid var(--border, #e5e7eb)', paddingTop: 12 }}>
      <h3 style={{ fontSize: 13, margin: '0 0 4px' }}>Apply to payroll already posted</h3>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        The rule above only fires when a run posts. Runs posted before it existed carry no
        charge-back; preview a pay-date range and apply to add it to each of those stubs. Stubs
        already charged, and anyone not listed above, are skipped.
      </p>
      <div className="form-grid" style={{ maxWidth: 480 }}>
        <label htmlFor="or-from">Pay date from</label>
        <input id="or-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <label htmlFor="or-to">Pay date to</label>
        <input id="or-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <button type="button" className="btn-secondary" disabled={!ready || run.isPending} onClick={doPreview}>
          Preview
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={!preview || preview.totals.planned === 0 || run.isPending}
          onClick={doApply}
        >
          {run.isPending ? 'Working…' : `Apply ${preview?.totals.planned ?? 0}`}
        </button>
      </div>
      {note && <div className="field-error" style={{ marginTop: 8 }}>{note}</div>}
      {applied && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <span className="tag ok">Applied {applied.applied.length}</span>{' '}
          <span className="muted">Skipped {applied.skipped.length}</span>{' '}
          {applied.failed.length > 0 && <span className="tag">Failed {applied.failed.length}</span>}
          {applied.failed.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {applied.failed.map((f) => (
                <li key={f.paystubId}>
                  {f.entryNumber} {f.employeeName} {formatAccountingDate(f.payDate)}: {f.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {preview && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            <strong>{preview.totals.planned}</strong> stub{preview.totals.planned === 1 ? '' : 's'} to
            charge back, totalling <strong>{formatCurrency(preview.totals.amountTotal)}</strong>.{' '}
            {preview.totals.skippedCount > 0 && (
              <span className="muted">{preview.totals.skippedCount} skipped.</span>
            )}
          </div>
          {preview.plan.length > 0 && (
            <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Pay date</th>
                    <th>Employee</th>
                    <th style={{ textAlign: 'right' }}>Employer cost</th>
                    <th style={{ textAlign: 'right' }}>Share</th>
                    <th style={{ textAlign: 'right' }}>Charge-back</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.plan.map((p) => (
                    <tr key={p.paystubId}>
                      <td className="muted">{p.entryNumber}</td>
                      <td>{formatAccountingDate(p.payDate)}</td>
                      <td>{p.employeeName}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(p.employerCost)}</td>
                      <td style={{ textAlign: 'right' }}>{p.sharePercent}%</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.skipped.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>Skipped ({preview.skipped.length})</summary>
              <table className="table">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Pay date</th>
                    <th>Employee</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.skipped.map((s) => (
                    <tr key={s.paystubId}>
                      <td className="muted">{s.entryNumber ?? ''}</td>
                      <td>{formatAccountingDate(s.payDate)}</td>
                      <td>{s.employeeName}</td>
                      <td className="muted">{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
